import Tafqeet from '@/plugins/tafqeet';
import { cloneDeep } from 'lodash';
import oidcConfig from './oidc.config';
import moment from 'moment-hijri';

export const isLocalDevelopment = () => process.env.NODE_ENV === 'local';
export const isLocalSslDevelopment = () => process.env.NODE_ENV === 'localSsl';
export const isDevelopment = () => process.env.NODE_ENV === 'development';
export const isDockerImage = () => process.env.NODE_ENV === 'docker';
const isCustomTokenUsed = () => !!process.env.VUE_APP_CUSTOM_TOKEN;
const customToken = () => isCustomTokenUsed() ? process.env.VUE_APP_CUSTOM_TOKEN : null;

export const uuidGenerator = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const deepClone = object => (object ? cloneDeep(object) : null);

export const isObjectEmpty = object => !object || !Object.keys(object).length;

export const stringToKebabCase = (str) => {
  if (!str) return null;
  const strReplaced = str.toString()
    .replace(/\.| |:|\|/g, '-');
  return strReplaced
    .replace(/(?!^)[A-Z]/g, '-$&')
    .replace(/^-/, '')
    .replace(/-\s*$/, '')
    .replaceAll('(', '')
    .replaceAll(')', '')
    .toLowerCase();
};

export const generateHtmlTagId = (elem, prefix) => {
  const id = [prefix];

  id.push(stringToKebabCase(elem.$parent.title));
  id.push(stringToKebabCase(elem.title));
  id.push(stringToKebabCase(elem.$attrs.label || elem.$props.label));

  const technicalName = elem.$attrs['technical-name'] || elem.$props['technical-name'];
  technicalName && id.push(stringToKebabCase(technicalName));

  if (elem.$slots.default && elem.$slots.default.length) { id.push(stringToKebabCase(elem.$slots.default[0].text)); }

  return id.filter(x => !!x)
    .join('-');
};

export const convertToValidTechnicalName = str => (str || '').replace(/[^a-z0-9_]/gi, '');

export const getCookie = (name) => {
  const v = document.cookie.match(`(^|;) ?${name}=([^;]*)(;|$)`);
  return v ? v[2] : null;
};

const getLocalStorageParsedItem = (key) => {
  const item = localStorage.getItem(key);
  return JSON.parse(item);
};

export const isAuthenticated = () => {
  if (isCustomTokenUsed()) return isCustomTokenUsed();

  const savedUser = getLocalStorageParsedItem(
    `oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`
  );
  const isExpired = !savedUser || savedUser.expires_at < Date.now() / 1000;

  return Boolean(savedUser) && Boolean(savedUser.access_token) && !isExpired;
};

export const getAccessToken = (withType = true) => {
  if (!isAuthenticated()) return;
  if (isCustomTokenUsed()) return (withType ? 'Bearer ' : '') + customToken();

  const savedUser = getLocalStorageParsedItem(
    `oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`
  );

  return `${withType ? savedUser.token_type + ' ' : ''}${savedUser.access_token}`;
};

export const listenForSessionChange = () => {
  if (!isDevelopment()) {
    window.addEventListener('storage', (e) => {
      if (e.key?.includes(`oidc.user:`)) location.reload();
    }, false);
  }
};

export const resetAccessToken = () => {
  localStorage.removeItem(`oidc.user:${oidcConfig.authority}:${oidcConfig.client_id}`);
};

export const convertToEnglish = (string) => {
  return string
    .replace(/[\u0660-\u0669]/g, c => c.charCodeAt(0) - 0x0660)
    .replace(/[\u06f0-\u06f9]/g, c => c.charCodeAt(0) - 0x06f0);
};

export const scrollIntoInValidInput = (errors) => {
  const firstInvalidField = Object.keys(errors.collect())[0];
  if (!firstInvalidField || firstInvalidField === 'null') return;
  document
    .querySelector(`[id*='${firstInvalidField}']`)
    .parentElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
};

export const executeString = (expression, obj = {}) => {
  const Vue = require('@/main').default;
  const axios = require('axios');
  try {
    try {
      return eval(expression);
    } catch {
      return eval(`"${expression}"`);
    }
  } catch (e) {
    console.warn(`Error: ${e}\nWhile executing script: ${expression}`);
    return null;
  }
};

export const printElementContent = (elementContent) => {
  let styleSheetList = '';
  document.getElementsByTagName('style')
    .forEach((styleElem) => {
      styleSheetList += styleElem.outerHTML;
    });
  const printWindow = window.open('', 'print-window', 'height=640,width=875');

  printWindow.document.write('<html dir="rtl">');
  printWindow.document.write(`<head><title>${document.title}</title>${styleSheetList}</head>`);
  printWindow.document.write(`<body>${elementContent}</body>`);
  printWindow.document.write('</html>');

  setTimeout(() => {
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, 500);
};

export const printPageByUrl = (url) => {
  window.open(url, 'print-window', 'height=640,width=875');
};

export const convertEnToArChar = (char) => {
  try {
    return ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'][+char];
  } catch (error) {
    return char;
  }
};

const modulePrefixes = {
  ADMINISTRATION: '/administration',
  CASES: '/cases',
  INBOX: '/inbox',
  CASES_REQUESTS: '/CasesRequests',
  REQUESTS: '/requests',
  SYSTEM: '/system',
  ICMS: '/icms',
  JUDICIALFEES: '/judicialFees',
  BANKRUPTCY: '/bankruptcy',
  SUPPORT: '/support',
  TASKMANAGER: '/taskmanager',
};

const localSslModulePrefixes = {
  ADMINISTRATION: 'https://localhost:44318',
  CASES: 'https://localhost:44390',
  INBOX: 'https://localhost:44399',
  REQUESTS: 'https://localhost:44311',
  SYSTEM: 'https://localhost:44339',
  ICMS: 'https://localhost:44304',
  JUDICIALFEES: 'https://localhost:44305',
  BANKRUPTCY: '/bankruptcy',
  SUPPORT: '/support',
  TASKMANAGER: '/taskmanager',
};

const localModulePrefixes = {
  ADMINISTRATION: 'http://localhost:60601',
  CASES: 'http://localhost:60603',
  INBOX: 'http://localhost:60602',
  REQUESTS: 'http://localhost:60604',
  CASES_REQUESTS: 'http://localhost:60605',
  SYSTEM: 'http://localhost:60600',
  ICMS: 'http://localhost:60606',
  JUDICIALFEES: 'http://localhost:60608',
  BANKRUPTCY: 'http://localhost:60610',
  SUPPORT: 'http://localhost:60609',
  TASKMANAGER: 'http://localhost:60612',
};

const dockerModulePrefixes = {
  ADMINISTRATION: 'http://localhost:7001',
  CASES: 'http://localhost:7002',
  INBOX: 'http://localhost:7006',
  REQUESTS: 'http://localhost:7004',
  SYSTEM: 'http://localhost:7000',
  CASES_REQUESTS: '',
  ICMS: '',
  JUDICIALFEES: '',
  BANKRUPTCY: '',
  SUPPORT: '',
  TASKMANAGER: '',
};

export const modulesBaseUrls = (() => {
  if (isLocalDevelopment()) return localModulePrefixes;
  if (isLocalSslDevelopment()) return localSslModulePrefixes;
  if (isDockerImage()) return dockerModulePrefixes;
  return modulePrefixes;
})();

export const apiModule = (() => {
  const apiModules = {};
  for (const key in modulesBaseUrls)
    if (Object.hasOwnProperty.call(modulesBaseUrls, key))
      apiModules[key] = `${modulesBaseUrls[key]}/api`;
  return apiModules;
})();

export const convertNumberToText = number => Tafqeet(number);

export const getNestedValidators = (vueElement) => {
  const validators = [];
  if (vueElement.$validator) validators.push(vueElement.$validator.validateAll());
  if (Array.isArray(vueElement.$children) && vueElement.$children.length)
    vueElement.$children.forEach(child => validators.push(...getNestedValidators(child)));
  return validators;
};

export const isValidDate = date => isNaN(+date)
  && isNaN(Date.parse(date)) == false
  && !Array.isArray(date)
  && !/[\u0600-\u06FF]/.test(date);
export const isValidMiladyDate = date => isValidDate(date) && Date.parse(date) >= -3471300309000;
export const isValidHijriDate = date => isValidDate(date) && Date.parse(date) < -3471300309000;

const currentTime = () => Date.now();
const expiresAfter = currentTime() - process.env.VUE_APP_LOOKUPS_CACHING_SEC * 1000;

export const encode = (value) => {
  if (isDevelopment() || isLocalDevelopment() || isDockerImage())
    return JSON.stringify(value);
  else
    return btoa(unescape(encodeURIComponent(JSON.stringify(value))));
};

export const decode = (value) => {
  if (isDevelopment() || isLocalDevelopment() || isDockerImage())
    return JSON.parse(value);
  else
    return JSON.parse(decodeURIComponent(escape(atob(value))));
};

const $localStorage = {};
export const setInLocalStorage = (key, value) => {
  const object = {
    accessToken: getAccessToken(),
    dateTime: currentTime(),
    value: value,
  };
  $localStorage[`naj-${key}`] = encode(object);
  setTimeout(async () => {
    try {
      localStorage.setItem(encode(`naj-${key}`), encode(object));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Local Storage error: ', err);
    }
  }, 0);
};
export const getFromLocalStorage = (key, defaultValue) => {
  const item = $localStorage[`naj-${key}`] || localStorage.getItem(encode(`naj-${key}`));
  if (item) {
    try {
      const object = decode(item);
      if (object.accessToken !== getAccessToken() || object.dateTime < expiresAfter) {
        throw new Error();
      } else {
        $localStorage[`naj-${key}`] = item;
        return object.value;
      }
    } catch (e) {
      $localStorage[`naj-${key}`] = undefined;
      localStorage.removeItem(encode(`naj-${key}`));
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
};
export const removeAllStartingByKeyFromLocalStorage = (key = '') =>
  new Promise((resolve) => {
    const encodedKey = encode(`${key.startsWith('naj-') ? '' : 'naj-'}${key}`)
      .slice(0, -1)
      .slice(0, -1);
    const removedKeys = [];
    for (const lsKey in localStorage) {
      if (Object.hasOwnProperty.call(localStorage, lsKey) && lsKey.startsWith(encodedKey)) {
        localStorage.removeItem(lsKey);
        removedKeys.push(decode(lsKey));
      }
    }
    resolve(removedKeys);
  });

export const openInNewTab = (context, path) => {
  let routeData = context.$router.resolve({ path: path });
  window.open(routeData.href, '_blank');
};

export const roundNumber = (value, places = 2) => {
  return +(Math.round(value + 'e+' + places) + 'e-' + places);
};

export const toHijriDateString = (date) => {
  if (!date) return '';
  const hijriDate = moment(date)
    .format('iYYYY/iMM/iDD');
  const gregorianDate = moment(date)
    .format('YYYY/MM/DD مـ');
  return hijriDate.includes('NaN') ? gregorianDate : hijriDate;
};

export const toHijriDateEnglishNumber = (date) => {
  if (!date) {
    return '';
  }
  const currentLocale = moment;
  currentLocale.locale('en-SA');
  const hijriDate = currentLocale(date)
    .format('iYYYYiMMiDD');
  return hijriDate;
};

export const getArabicTime = (date) => {
  if (!date) return '';
  const currentLocale = moment.locale();
  moment.locale('ar-SA');
  const info = moment(date, 'YYYY-MM-DD HH:mm:ss')
    .format('HH:mm A');
  moment.locale(currentLocale);
  return info;
};

export const toHijriDate = (date) => {
  if (!date) return '';
  const hijriDate = moment(date)
    .format('iYYYY/iMM/iDD');
  const gregorianDate = moment(date)
    .format('YYYY/MM/DD مـ');
  return hijriDate.includes('NaN') ? gregorianDate : hijriDate;
};